import ArticleMeta from '../types/ArticleMeta';

declare global {
  interface Window {
    dataLayer: object[];
  }
}

export function addEventToGTMDataLayer(event: object) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(event);
}

// Article card click related logic -->
export const articleCardLocation = {
  STAFF_PICKS: 'staff_picks',
  LATEST: 'latest',
  INCOMING: 'incoming',
  MAIN_CONTENT: 'main_content',
  NEWS: 'news',
  ARTICLE_FOOTER_RELATED: 'article_footer_related',
} as const;

export type ArticleCardLocation =
  (typeof articleCardLocation)[keyof typeof articleCardLocation];

export const articleCardClickTarget = {
  ARTICLE: 'article',
  AUTHOR: 'author',
  CATEGORY: 'category',
};

export type ArticleCardClickTargetType =
  (typeof articleCardClickTarget)[keyof typeof articleCardClickTarget];

/**
 *
 * @param articleMeta
 * @param target the click target area of the article click
 * @param location the location of the article card in the page
 * @param index the index of the article in the list, starting from 0
 */
export function sendArticleCardClickToGTM(
  articleMeta: ArticleMeta,
  target: ArticleCardClickTargetType,
  location?: ArticleCardLocation,
  index?: number,
) {
  addEventToGTMDataLayer({
    event: 'article_card_click',
    article_id: articleMeta.id,
    article_title: articleMeta.title,
    article_card_click_target: target,
    article_card_location: location,
    article_card_list_position: index ? index + 1 : undefined,
  });
}

export const GTM_CONTAINERS = {
  JAA: 'GTM-5TDV9VBT',
  VKH: 'GTM-TX2WXLZ', // Replace with Service B's container ID
} as const;

export type GTMContainer = (typeof GTM_CONTAINERS)[keyof typeof GTM_CONTAINERS];

let currentContainer: GTMContainer | null = null;

export const initializeGTM = (containerId: GTMContainer): void => {
  // If already initialized with the same container, skip
  if (currentContainer === containerId) {
    return;
  }

  // If switching containers, warn and reload
  if (currentContainer && currentContainer !== containerId) {
    /* This is a defensive guard: currently, ReadingView and the main app have no
    React Router links between them, so this reload should never occur during
    normal operation. If this triggers, it indicates the navigation architecture
    has changed and a better GTM container management strategy should be implemented. */
    console.warn(
      `GTM container mismatch: tried to switch from ${currentContainer} to ${containerId}. Reloading page.`,
    );
    window.location.reload();
    return;
  }

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  });

  // Inject GTM script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;

  const firstScript = document.getElementsByTagName('script')[0];
  firstScript.parentNode?.insertBefore(script, firstScript);

  // Inject noscript iframe
  const noscript = document.createElement('noscript');
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${containerId}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  noscript.appendChild(iframe);

  document.body.insertBefore(noscript, document.body.firstChild);

  currentContainer = containerId;
};

export const getGTMContainerForPath = (pathname: string): GTMContainer => {
  if (pathname.startsWith('/lue/')) {
    return GTM_CONTAINERS.VKH;
  }

  return GTM_CONTAINERS.JAA;
};

export const validateGTMContainer = (pathname: string): void => {
  const expectedContainer = getGTMContainerForPath(pathname);

  if (currentContainer && currentContainer !== expectedContainer) {
    /* Wrong GTM container for route {pathname}. Expected {expectedContainer},
    got {currentContainer}. Reloading to load correct container. */
    window.location.reload();
  }
};
